import React, { useState, useEffect } from 'react';
import { FaSearch } from 'react-icons/fa';
import { FaCheck } from 'react-icons/fa6';
import { FaTimes, FaPlus } from 'react-icons/fa';
import BotaoPadrao from '../BotaoPadrao/BotaoPadrao';
import InputPadrao from '../InputPadrao/InputPadrao';
import DropdownPadrao from '../DropdownPadrao/DropdownPadrao';
import TabelaPadrao from '../TabelaPadrao/TabelaPadrao';
import TabelaPadraoDividida from '../TabelaPadraoDividida/TabelaPadraoDividida';
import BotaoAdicionarVinculos from '../BotaoAdicionarVinculos';
import CheckboxPadrao from '../CheckboxPadrao/CheckboxPadrao';
import Tooltip from '../Tooltip/Tooltip';
import Carregamento from '../Carregamento/Carregamento';
import PAGINATION, { paginarLista, obterPagina } from '../../utils/PaginacaoUtils';
import { OPCOES_SITUACAO } from '../../utils/ListagemUtils';
import api from '../../services/api';
import "./ModalBuscaRelatorio.css";

// Tipo de modal para o componente de busca de relatórios
export const TIPO_MODAL_RELATORIO = {
    USUARIO: "Usuários"
};

export default function ModalBuscaRelatorio({
    tipoModal = TIPO_MODAL_RELATORIO.USUARIO,
    onCancelar,
    onSalvar,
    itensNaoVinculados = [],
    itensVinculados = [],
    loading = false,
    totalRegistros = 0,
    paginaAtual = 0,
    tamanhoPagina = PAGINATION.DEFAULT_PAGE_SIZE,
    proximaPagina,
    paginaAnterior,
    irParaPagina,
    onPaginaChange, // Callback para notificar o componente pai sobre mudanças de página
    textoTopo
}) {
    const [filtroNome, setFiltroNome] = useState("");
    const [filtroLogin, setFiltroLogin] = useState("");
    const [filtroSituacao, setFiltroSituacao] = useState("");
    const [itensFiltrados, setItensFiltrados] = useState(itensNaoVinculados);
    const [totalRegistrosLocal, setTotalRegistrosLocal] = useState(totalRegistros || 0);
    const [loadingBusca, setLoadingBusca] = useState(false);
    
    const [selecionados, setSelecionados] = useState(itensVinculados);
    
    // Estados para paginação local na tabela da direita
    const [paginasLocais, setPaginasLocais] = useState([]);
    const [paginaAtualLocal, setPaginaAtualLocal] = useState(0);
    const [itensPaginadosLocal, setItensPaginadosLocal] = useState([]);

    // Nomes e títulos para o modal
    const nomes = {
        tituloLista: "Lista de Usuários",
        tituloAdicionados: "Usuários adicionados",
        placeholderBusca: "Procure por usuário.",
        labelBusca: "Usuário",
    };
    
    // Função para lidar com a tecla Enter nos campos de input
    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handlePesquisarItens();
        }
    };

    // Opções para o dropdown de situação cadastro
    // Usando as mesmas opções definidas em ListagemUtils para padronização
    const situacaoOptions = OPCOES_SITUACAO;

    // Atualiza itens filtrados ao abrir modal ou mudar itensNaoVinculados
    useEffect(() => {
        setItensFiltrados(itensNaoVinculados);
    }, [itensNaoVinculados]);

    // Atualiza total de registros quando a prop totalRegistros mudar
    useEffect(() => {
        setTotalRegistrosLocal(totalRegistros || 0);
    }, [totalRegistros]);

    // Atualiza selecionados quando itensVinculados mudar
    useEffect(() => {
        setSelecionados(itensVinculados);
    }, [itensVinculados]);

    // Atualiza paginação quando paginaAtual mudar
    useEffect(() => {
        if (onPaginaChange && paginaAtual !== undefined) {
            // Apenas notifica o componente pai sobre a mudança, sem fazer uma nova requisição
            // Isso é importante para manter a UI sincronizada
            onPaginaChange(paginaAtual);
        }
    }, [paginaAtual, onPaginaChange]);
    
    // Atualiza paginação local quando selecionados mudar
    useEffect(() => {
        // Este efeito implementa a paginação local na tabela da direita
        // usando as funções do PaginacaoUtils
        
        // Calcula as páginas com base na lista completa
        const paginas = paginarLista(selecionados, tamanhoPagina);
        setPaginasLocais(paginas);
        
        // Verifica se a página atual ainda é válida
        const novaPaginaAtual = paginaAtualLocal < paginas.length ? paginaAtualLocal : 0;
        setPaginaAtualLocal(novaPaginaAtual);
        
        // Atualiza os itens exibidos na tabela
        setItensPaginadosLocal(paginas.length > 0 ? obterPagina(paginas, novaPaginaAtual) : []);
    }, [selecionados, tamanhoPagina, paginaAtualLocal]);

    // Função para pesquisar itens com os termos digitados
    const handlePesquisarItens = async () => {
        setLoadingBusca(true);
        try {
            const endpoint = '/usuario_';
            
            const params = { page: 0, size: tamanhoPagina };
            if (filtroNome) params.nome = filtroNome;
            if (filtroLogin) params.login = filtroLogin;
            if (filtroSituacao !== "") {
                params.situacaoCadastro = filtroSituacao;
            }
            
            const response = await api.get(endpoint, { params });
            const apiResponse = response.data;
            if (response.success) {
                setItensFiltrados(apiResponse.content || []);
                setTotalRegistrosLocal(apiResponse.page.totalElements);
                
                // Notificar o componente pai sobre a mudança para a página 0
                if (onPaginaChange) {
                    onPaginaChange(0);
                }
            } else {
                setItensFiltrados([]);
                setTotalRegistrosLocal(0);
            }
        } catch (e) {
            console.error("Erro ao buscar usuários:", e);
            setItensFiltrados([]);
        } finally {
            setLoadingBusca(false);
        }
    };

    // Função para vincular um usuário (adicionar à lista de selecionados)
    const vincularItem = (item) => {
        if (!selecionados.some(s => s.idUsuario === item.idUsuario)) {
            setSelecionados(prev => [...prev, {
                ...item,
                nome: item.nome,
                descricao: item.descricao
            }]);
        }
    };

    // Função para desvincular um usuário (remover da lista de selecionados)
    const desvincularItem = (item) => {
        setSelecionados(prev => prev.filter(s => s.idUsuario !== item.idUsuario));
    };

    // Função para verificar se um usuário está vinculado
    const isItemVinculado = (item) => {
        return selecionados.some(s => s.idUsuario === item.idUsuario);
    };

    // Função para obter o ID do item
    const getItemId = (item) => item.idUsuario;
    
    // Função para obter a descrição do item
    const getItemDescription = (item) => item.nome || item.descricao;

    // Colunas da tabela
    const colunas = [
        { campo: 'nome', titulo: 'Nome' },
        { campo: 'login', titulo: 'Login' }
    ];

    // Função para buscar todos os usuários do backend com os filtros atuais
    const fetchAllUsuarios = async () => {
        setLoadingBusca(true);
        try {
            const endpoint = '/usuario_/selectAll';
            const params = {};
            if (filtroNome) params.nome = filtroNome;
            if (filtroLogin) params.login = filtroLogin;
            if (filtroSituacao !== "") {
                // Enviar o valor enum diretamente para o backend (ATIVO ou INATIVO)
                params.situacaoCadastro = filtroSituacao;
            }
            
            const response = await api.get(endpoint, { params });
            const apiResponse = response.data;
            
            // Para o endpoint selectAll, pode ser que venha direto o array
            if (Array.isArray(apiResponse.content)) {
                return apiResponse.content;
            }
            return apiResponse;
        } catch (e) {
            console.error("Erro ao buscar todos os usuários:", e);
            return [];
        } finally {
            setLoadingBusca(false);
        }
    };

    // Função para selecionar todos os usuários filtrados
    const handleSelecionarTodos = async () => {
        // Mostrar carregamento
        setLoadingBusca(true);
        try {
            if (totalRegistrosLocal > tamanhoPagina) {
                // Buscar todos do backend
                const todos = await fetchAllUsuarios();
                setSelecionados(prev => {
                    const novos = todos.filter(item => !prev.some(s => s.idUsuario === item.idUsuario));
                    return [...prev, ...novos];
                });
                
                // Reset para a primeira página após adicionar todos
                setPaginaAtualLocal(0);
            } else {
                // Comportamento padrão para uma página
                setSelecionados(prev => {
                    const novos = itensFiltrados.filter(item => !prev.some(s => s.idUsuario === item.idUsuario));
                    return [...prev, ...novos];
                });
            }
        } catch (error) {
            console.error("Erro ao selecionar todos:", error);
        } finally {
            setLoadingBusca(false);
        }
    };



    // Função para remover todos os usuários selecionados
    const handleRemoverTodos = () => {
        setSelecionados([]);
    };
    
    // Funções para navegação na paginação local
    const proximaPaginaLocal = () => {
        if (paginaAtualLocal < paginasLocais.length - 1) {
            const novaPagina = paginaAtualLocal + 1;
            setPaginaAtualLocal(novaPagina);
            setItensPaginadosLocal(obterPagina(paginasLocais, novaPagina));
        }
    };
    
    const paginaAnteriorLocal = () => {
        if (paginaAtualLocal > 0) {
            const novaPagina = paginaAtualLocal - 1;
            setPaginaAtualLocal(novaPagina);
            setItensPaginadosLocal(obterPagina(paginasLocais, novaPagina));
        }
    };
    
    const irParaPaginaLocal = (pagina) => {
        if (pagina >= 0 && pagina < paginasLocais.length) {
            setPaginaAtualLocal(pagina);
            setItensPaginadosLocal(obterPagina(paginasLocais, pagina));
        }
    };

    return (
        <div className="modal-busca-container">
            {loadingBusca && (
                <div className="modal-carregamento-overlay">
                    <Carregamento />
                </div>
            )}
            <div className="modal-busca-cabecalho">
                <span>{nomes.textoTopo}</span>
                
                <div className="modal-busca-filtros">
                    <div className="modal-busca-filtros-group">
                        <InputPadrao 
                            label="Nome"
                            value={filtroNome} 
                            onChange={e => setFiltroNome(e.target.value)}
                            onKeyDown={handleKeyPress}
                            placeholder="Digite o nome do usuário"
                        />
                        <InputPadrao 
                            label="Login" 
                            value={filtroLogin} 
                            onChange={e => setFiltroLogin(e.target.value)}
                            onKeyDown={handleKeyPress}
                            placeholder="Digite o login do usuário"
                        />
                        <DropdownPadrao 
                            label="Situação cadastro" 
                            value={filtroSituacao} 
                            onChange={e => setFiltroSituacao(e.target.value)}
                            options={situacaoOptions}
                            placeholder="Selecione a situação"
                        />
                    </div>
                    
                    <div className="search-button-container">
                        <BotaoPadrao
                            aoClicar={handlePesquisarItens}
                            icone={<FaSearch />}
                            texto="Pesquisar"
                            confirmar={true}
                            classeCustom="botao-pesquisar"
                        />
                    </div>
                </div>
            </div>
            
            <div className="modal-busca-content">
                <TabelaPadraoDividida
                    tabelaEsquerda={{
                        titulo: nomes.tituloLista,
                        dados: itensFiltrados,
                        colunas: colunas,
                        totalRegistros: totalRegistrosLocal,
                        paginaAtual: paginaAtual,
                        tamanhoPagina: tamanhoPagina,
                        proximaPagina: proximaPagina,
                        paginaAnterior: paginaAnterior,
                        irParaPagina: irParaPagina,
                        loading: loading || loadingBusca,
                        // Props para botão de adicionar
                        acaoPersonalizada: (item) => (
                            <BotaoAdicionarVinculos
                                adicionar={true} // Sempre mostrar o ícone de adicionar independentemente do estado do item
                                onClick={() => {
                                    vincularItem(item);
                                }}
                            />
                        ),
                        onAcao: vincularItem,  // Adiciona onAcao para compatibilidade com TabelaPadraoDividida
                        getItemId: getItemId,
                        getItemDescription: getItemDescription,
                        linhaDestaque: (item) => isItemVinculado(item),
                        acaoCabecalho: (
                            <Tooltip text={totalRegistrosLocal > tamanhoPagina ? 
                                "Selecionar todos os usuários" : 
                                "Selecionar todos desta página"}>
                                <BotaoAdicionarVinculos
                                    adicionar={true}
                                    onClick={handleSelecionarTodos}
                                />
                            </Tooltip>
                        )
                    }}
                    tabelaDireita={{
                        titulo: nomes.tituloAdicionados,
                        dados: itensPaginadosLocal.length > 0 ? itensPaginadosLocal : selecionados.slice(0, tamanhoPagina),
                        colunas: colunas,
                        totalRegistros: selecionados.length,
                        paginaAtual: paginaAtualLocal,
                        tamanhoPagina: tamanhoPagina,
                        loading: false,
                        // Props para navegação de página local
                        proximaPagina: proximaPaginaLocal,
                        paginaAnterior: paginaAnteriorLocal,
                        irParaPagina: irParaPaginaLocal,
                        // Props para botão de remover
                        acaoPersonalizada: (item) => (
                            <BotaoAdicionarVinculos
                                adicionar={false} // Sempre mostrar o ícone de remover para itens na tabela da direita
                                onClick={() => {
                                    desvincularItem(item);
                                }}
                            />
                        ),
                        onAcao: desvincularItem,  // Adiciona onAcao para compatibilidade com TabelaPadraoDividida
                        getItemId: getItemId,
                        getItemDescription: getItemDescription,
                        acaoCabecalho: (
                            <Tooltip text="Remover todos">
                                <BotaoAdicionarVinculos
                                    adicionar={false}
                                    onClick={handleRemoverTodos}
                                />
                            </Tooltip>
                        )
                    }}
                    exibeAcao={false}
                    desabilitarEstiloBotaoEditar={true}
                    modeloNovo={true}
                />
            </div>
            
            <div className="modal-busca-botoes">
                <BotaoPadrao
                    cancelar={true}
                    aoClicar={onCancelar}
                    texto="Cancelar"
                />
                <BotaoPadrao
                    icone={<FaCheck />}
                    confirmar={true}
                    aoClicar={() => onSalvar({
                        itensVinculados: selecionados,
                        tipoModal
                    })}
                    texto="Confirmar"
                />
            </div>
        </div>
    );
}
